import {inject, Injectable} from './di';
import {AppInitializeService} from './app-initialize-service';
import {Logger} from './logger';
import {ConfigService} from './config/config-service';
import {LegacyLogService} from './logger/legacy-log-service';
import {WebSocketClient} from './web-socket-client';
import {ServiceCodesService} from './service-codes-service';
import {StorageService} from './storage-service';
import {HotelInfoService} from './hotel-info-service';
import {RoomNumberService} from './room-number-service';
import {LegacyPersistentLogCategory, LegacyPersistentLogService} from './logger/legacy-persistent-log-service';
import {NetworkInfoService} from './network-info-service';
import {CustomKeyType, KeyType} from '../types/key-type';
import {LanguageCode} from './i18n/i18n-service';
import {post} from './utils/post';
import {isset} from './utils/isset';

@Injectable()
export class EventsService implements AppInitializeService {
  private readonly logger = inject(Logger);
  private readonly configService = inject(ConfigService);
  private readonly legacyLogService = inject(LegacyLogService);
  private readonly legacyPersistentLogService = inject(LegacyPersistentLogService);
  private readonly serviceCodesService = inject(ServiceCodesService);
  private readonly storageService = inject(StorageService);
  private readonly hotelInfoService = inject(HotelInfoService);
  private readonly roomNumberService = inject(RoomNumberService);
  private readonly networkInfoService = inject(NetworkInfoService);

  /**
   * Local Storage key for storing current TVID
   */
  private readonly tvidLocalStorageKey = 'TVID';

  /**
   * List of TVID names
   */
  private tvidNames = [
    'not set',
    'Bedroom',
    'Living Room',
    'Bathroom',
    'TV 4',
    'TV 5',
    'TV 6',
    'TV 7',
    'TV 8',
    'TV 9',
    'TV 10',
  ];

  /**
   * Event listeners
   */
  private listeners = new Map<EventCategory, ServerEventHandler[]>();

  private listening = false;
  private listener: JQuery.jqXHR | null = null;
  private last_msg = {tag: 0, time: 0};
  private ws: WebSocket | null = null;
  private wsClient: WebSocketClient<WebSocketDeviceCommandMessage> | null = null;
  private listenTimeoutAttempt: number = 0;
  private secret: string = 'hoteza';
  private channel: string = 'undef';

  constructor() {
    window.Events = this;
  }

  public async init() {
    try {
      const config = this.configService.config;

      // Setup TVID names
      const configTvids = config.tv?.tvids;
      if (configTvids && configTvids.length > 0) {
        configTvids.forEach((name, tvid) => {
          if (name) {
            this.tvidNames[tvid] = name;
          }
        });
      } else {
        this.logger.debug('[EventsService] config.tv.tvids was not set, using default set', this.tvidNames);
      }

      this.secret = config.secret || 'hoteza';

      this.serviceCodesService.registerListener({
        '2109': () => {
          const tvid = this.TVID();
          const name = this.getTVIDName();
          let tvidStr: string;
          if (name) {
            tvidStr = name + ` (${tvid})`;
          } else {
            tvidStr = tvid.toString();
          }
          window.custom_dialog(
            'info',
            'Events',
            `Channel: ${this.channel}<br>Listening: ${this.listening}<br>TVID: ${tvidStr}`,
          );
        },
        '2100': () => {
          this.TVID(0);
        },
        '2101': () => {
          this.TVID(1);
        },
        '2102': () => {
          this.TVID(2);
        },
        '2103': () => {
          this.TVID(3);
        },
        '2104': () => {
          this.TVID(4);
        },
        '2105': () => {
          this.TVID(5);
        },
        '2106': () => {
          const config = this.configService.config;
          const tvids = config.tv?.tvids || this.tvidNames;
          const currentTvid = this.TVID() || 0;

          let tmp = '<div id="custom_dialog_buttons_wrapper">';

          tvids
            .map(
              (tvidName, tvid) =>
                `
<div class="button"
     clickable
     style="padding-left:10px;padding-right:10px;box-sizing: border-box;"
     onvclick="Events.TVID(${tvid})">
  ${tvid == currentTvid ? '&rarr; ' + tvidName + ' &larr;' : tvidName}
</div>`,
            )
            .join('');

          tmp += '</div><br>';

          window.custom_dialog('info', 'TVID Setup', tmp);
          //$('#custom_dialog_ok').remove();
        },
      });

      this.registerListener(EventCategory.Sys, this.processCmd.bind(this));

      // NOTE: it's not changes when we change TVID. Is it okay?
      document.body.dataset.tvId = this.TVID().toString();

      const room = this.roomNumberService.roomNumber;
      const hotelId = this.hotelInfoService.hotelId;
      if (room) {
        this.channel = 'hotel' + hotelId + 'room' + room;
        this.listen();
        if (this.configService.config.eventBridge?.enabled) {
          void this.listenWs();
        }
      } else {
        this.legacyLogService.add('Events: init without room#');
        this.logger.warn('[EventsService] Unable to initialize because room number was not set.');
      }
    } catch (err) {
      this.logger.error('[EventsService] Unable to initialize', err);
    }

    return Promise.resolve(true);
  }

  /**
   * Subscribe for events.
   * You could add multiple listeners for single category.
   * @param category category
   * @param callback callback
   */
  public registerListener(category: EventCategory, callback: ServerEventHandler) {
    if (!category) {
      this.logger.error(`[EventsService] Unable to register listener for empty category`, callback);
      this.legacyLogService.add('Events: category cannot be empty');
      return;
    }

    if (!callback || typeof callback !== 'function') {
      this.logger.error(`[EventsService] Unable to register listener, callback was not set`, category);
      return;
    }

    let listeners = this.listeners.get(category);
    if (!listeners) {
      listeners = [];
      this.listeners.set(category, listeners);
    }
    this.logger.debug(`[EventsService] Registering event handler for "${category}" category...`);

    listeners.push(callback);
  }

  public async send(cmd: string, data?: string, category: EventCategory = EventCategory.RC) {
    data = data || '';
    category = category || EventCategory.RC;
    const room = this.roomNumberService.roomNumber;
    const config = this.configService.config;
    const hotelId = this.hotelInfoService.hotelId;

    const tmp = window.GibberishAES.enc(
      JSON.stringify({
        cat: category,
        room: room,
        ch: this.TVID() || '',
        cmd: cmd,
        data: data || '',
      }),
      this.secret,
    );

    const payload = JSON.stringify({c: tmp});

    if (!config.queue_url) {
      this.logger.error(`[EventsService.send] Unable to send, config.queue_url was not set`);
      return;
    }

    // TODO: local queue url?
    const p1 = post({url: `${config.queue_url}pub/?id=${this.channel}R`, data: payload});

    // Дублирование команд на широковещательный канал отеля
    const p2 = post({url: `${config.queue_url}pub/?id=hotel${hotelId}`, data: payload});

    await Promise.all([p1, p2]).catch((err) =>
      this.logger.error(`[EventsService.send] Unable to send to "${config.queue_url}"`, err),
    );
  }

  /**
   * Send message through WebSocket (Event Bridge).
   * Use this method to send events from TV to the server via WebSocket.
   * @param payload - Message payload to send
   * @returns {void}
   */
  public sendWs(payload: WebSocketResponseMessage): void {
    if (!this.configService.config.eventBridge?.enabled) {
      this.logger.warn('[EventsService.sendWs] Event Bridge is disabled');
      return;
    }

    if (!this.wsClient) {
      this.logger.warn('[EventsService.sendWs] WebSocket client is not initialized');
      return;
    }

    if (!this.wsClient.isOpen()) {
      this.logger.warn('[EventsService.sendWs] WebSocket is not connected, message dropped', payload);
      return;
    }

    this.wsClient.send(payload);
    this.logger.debug('[EventsService.sendWs] Message sent', payload);
  }

  public getTVIDName(): string {
    const tvid = this.TVID();
    return this.tvidNames[tvid] || '';
  }

  /**
   * Set TVID
   * @param id TVID index (index in config.tv.tvids array)
   */
  public TVID(id: number): void;

  /**
   * Get TVID
   */
  public TVID(): number;

  /**
   * Get or set TVID
   * @param id
   */
  public TVID(id?: number): number | undefined {
    if (id == null) {
      // getter logic
      return this.storageService.getNumber(this.tvidLocalStorageKey) || 0;
    }

    // setter logic
    id = id | 0; // force convert to int
    if (id) {
      const tvidName = this.tvidNames[id];
      this.logger.debug(`[EventsService.TVID] TVID was set to ${id}: ${tvidName || 'unknown TVID'}`);
      this.storageService.set(this.tvidLocalStorageKey, id);

      if (tvidName) {
        window.custom_dialog('info', 'TVID Setup', `TVID set to ${tvidName} (${id})`);
      } else {
        window.custom_dialog('info', 'TVID Setup', `TVID set to ${id} (unknown)`);
      }
    } else {
      this.logger.debug(`[EventsService.TVID] TVID cleared`);
      this.storageService.remove(this.tvidLocalStorageKey);
      window.custom_dialog('info', 'TVID Setup', 'TVID unset');
    }
  }

  /**
   * Silently set TVID, event if it is not defined in config.tv.tvids
   * @param id TVID
   */
  public forceSetTvid(id?: number | string) {
    if (typeof id === 'string') {
      id = parseInt(id);
    }
    if (id) {
      const tvidName = this.tvidNames[id];
      this.logger.debug(`[EventsService.TVID] TVID was set to ${id}: ${tvidName || 'unknown TVID'}`);
      this.storageService.set('TVID', id);
    } else {
      this.storageService.remove('TVID');
    }
  }

  /**
   * Process cmd
   * @param cmd
   * @private
   */
  private evaluate(cmd: ServerEventData): EvaluateResult {
    this.logger.debug(`[EventsService.evaluate] Call`, cmd);

    if (!this.isValidTVID(cmd.ch)) {
      this.logger.warn(`[EventsService.evaluate] Received unknown TVID: ${cmd.ch}`, cmd);
      // not for me
      return {status: false, message: 'Invalid TVID'};
    }

    const handlers = this.listeners.get(cmd.cat);

    if (handlers && handlers.length > 0) {
      for (const handler of handlers) {
        try {
          handler(cmd);
        } catch (err) {
          const message = `[EventsService] Unable to execute ${cmd.cat} Event callback , cmd: ${JSON.stringify(cmd)}, ${JSON.stringify(err)}`;

          this.logger.error(message);
          this.legacyLogService.add(message);

          return {status: false, message};
        }
      }
      return {status: true};
    } else {
      this.logger.warn(`[EventsService] Event ${cmd.cat} not registered`);
      this.legacyLogService.add('Events: received unregistered event [' + cmd.cat + ']');
      return {status: false, message: 'No handlers registered'};
    }
  }

  private listen(reinit?: boolean) {
    if (this.listening) {
      if (reinit) {
        this.listener?.abort();
        this.listener = null;
      } else {
        this.logger.warn(`[EventsService.listen] Already listening`);
        this.legacyLogService.add('Events: already listening');
        return false;
      }
    }

    this.listening = true;
    const queue_tag_time = `?tag=${this.last_msg.tag}&time=${this.last_msg.time}`;
    const config = this.configService.config;
    const url = `${config.queue_url}subv2/${this.channel}T${queue_tag_time}`;
    const longpollTimeout = parseInt(String(isset('config.tv.polling.events.longpoll_timeout', 20000)));
    const reconnectTimeout = parseInt(String(isset('config.tv.polling.events.reconnect_timeout', 20000)));

    this.listener = $.ajax({
      url: url,
      async: true,
      timeout: longpollTimeout,
      success: (data: Record<string, ApiEvent>) => {
        // NOTE: unable to find API schema, so `data` structure was extrapolated from code logic.
        //  And I'm still not sure if it is Record or Array

        this.listening = false;
        this.listenTimeoutAttempt = 0;

        for (const key in data) {
          const msg = data[key].text;
          if (msg) {
            try {
              const eventDataStr = window.GibberishAES.dec(msg.c, this.secret);

              const eventData = JSON.parse(eventDataStr) as ServerEventData;
              // TODO: разбор по подпискам cat
              if (eventData && eventData.cat) {
                this.evaluate(eventData);
              } else {
                //this.legacyLogService.add('RC: not for me');
                this.logger.warn(`[EventsService.listen] Unable to parse response, no category `, data);
              }

              this.last_msg = {
                tag: data[key].tag,
                time: data[key].time,
              };
            } catch (err) {
              this.logger.error(`[EventsService.listen] Bad response. Response:`, data, err);
              this.legacyLogService.add('Events: msg bad key');
            }
          }
        }
        setTimeout(() => {
          this.listen();
        }, 0);
      },
      dataType: 'json',
    }).fail((err, statusText) => {
      this.listening = false;
      this.logger.error(`[EventsService.listen] Request to ${url} was failed: ${statusText}`, err);

      if (statusText == 'timeout') {
        this.legacyLogService.add('Events: timeout');

        // Progressive time
        this.listenTimeoutAttempt++;
        const timeout = [1000, 1000, 1000, 2000, 3000, 5000, 10000][this.listenTimeoutAttempt] || 60000;
        setTimeout(() => {
          this.listen();
        }, timeout);
      } else {
        this.listenTimeoutAttempt = 0;
        this.legacyLogService.add(`Events: No connection: ${err.status}|${err.statusText} ${statusText}`);
        setTimeout(() => {
          this.listen();
        }, reconnectTimeout);
      }
    });
  }

  private getEventBridgeWsUrl(): string {
    const url = this.configService.config.eventBridge?.url || '';
    if (url.startsWith('ws://') || url.startsWith('wss://')) {
      return url;
    }
    return `wss://${url}`;
  }

  /**
   * New realization throw Nats
   * */
  private listenWs() {
    const hotelUid = this.configService.config.hotelUid;
    if (!hotelUid) {
      return this.logger.warn('[EventsService.listenWs] No hotel Uid found]');
    }

    this.wsClient = new WebSocketClient<WebSocketDeviceCommandMessage>({
      url: this.getEventBridgeWsUrl(),

      onOpen: (): void => {
        if (!this.wsClient) {
          this.logger.warn('[EventsService.listenWs] wsClient is null in onOpen callback');
          return;
        }
        this.wsClient.send({
          type: 'init',
          hotelId: hotelUid,
          roomId: window?.tv_room || 'UNKNOWN',
          deviceId: String(this.TVID()),
          mac: this.networkInfoService.mac,
          serialNumber: window._tv_get_info?.serial_number?.() || 'UNKNOWN',
          payload: {
            event: 'init',
            data: {
              isVirtualStandby: window.tv_virtual_standby_state,
              ip: this.networkInfoService.ip,
              networkType: this.networkInfoService.type,
            },
          },
        });
      },

      onMessage: (data: WebSocketDeviceCommandMessage): boolean => {
        if (!data.payload) {
          return false;
        }

        const result: EvaluateResult = this.evaluate(this.deserializePayload(data as CommandMessage));

        if (data.replyTo) {
          this.sendWs({
            type: 'response',
            replyTo: data.replyTo,
            correlationId: data.correlationId,
            state: result?.status || result ? 'executed' : 'failed',
            deviceState: {
              isVirtualStandby: window.tv_virtual_standby_state,
            },
          });
        }

        return true;
      },
    });

    this.wsClient.connect();
  }

  private deserializePayload(data: CommandMessage): ServerEventData {
    this.logger.debug(`[EventsService.deserializePayload] Received payload: ${JSON.stringify(data)}`);
    return {
      cat: data.module,
      ch: this.TVID(),
      cmd: data.payload.command,
      data: data.payload.data,
    };
  }

  private listen_ws(reinit?: boolean) {
    if (this.listening) {
      if (reinit) {
        this.ws?.close();
      } else {
        this.logger.log(`[EventsService.listen_ws] Already listening`);
        this.legacyLogService.add('EVWS: already listening');
        return false;
      }
    }

    const config = this.configService.config;
    const queue_tag_time = `?tag=${this.last_msg.tag}&time=${this.last_msg.time}`;
    this.ws = new WebSocket(
      'wss://' + config.queue_url!.replace(/^https?:\/\//, '') + 'subws/' + this.channel + 'T' + queue_tag_time,
    );
    this.ws.onopen = () => {
      this.listening = true;
      this.legacyLogService.add('EVWS: Connected');
      this.logger.debug(`[EventsService.listen_ws] WS connected`);
    };
    this.ws.onclose = (evt) => {
      this.listening = false;
      this.logger.debug(`[EventsService.listen_ws] WS connection closed`);
      this.legacyLogService.add('EVWS: CLOSE ' + evt.reason);
      // Maybe we should reconnect?
    };

    this.ws.onmessage = (evt) => {
      let data: ApiEvent;
      try {
        data = JSON.parse(evt.data as string) as ApiEvent;
      } catch (err) {
        this.logger.error(`[EventsService.listen_ws] Unable to parse WS message`, err);
        this.legacyLogService.add('WS: JSON parsing gone wrong');
        return false;
      }

      const msg = data.text;
      if (msg) {
        try {
          const eventDataStr = window.GibberishAES.dec(msg.c, this.secret);
          const eventData = JSON.parse(eventDataStr) as ServerEventData;
          //TODO: разбор по подпискам cat
          if (eventData && eventData.cat) {
            this.evaluate(eventData);
          } else {
            //this.legacyLogService.add('RC: not for me');
            this.logger.warn(`[EventsService.listen_ws] Unable to parse response: `, data);
          }
          this.last_msg = {
            tag: data.tag,
            time: data.time,
          };
        } catch (err) {
          this.legacyLogService.add('Events: msg bad key');
          this.logger.error(`[EventsService.listen_ws] Bad response. Response:`, data, err);
        }
      }
    };
    this.ws.onerror = (evt) => {
      this.listening = false;
      this.logger.error(`[EventsService.listen_ws] WS unexpected error`, evt);
      this.legacyLogService.add('EVWS: ERROR');
    };
  }

  private processCmd(obj: ServerEventData) {
    const config = this.configService.config;
    const cmd = obj.cmd;

    this.logger.debug(`[EventsService.processCmd] Incoming CMD event`, obj);
    if (cmd === CommandValue.TrialModeOn) {
      window.createTrialModeBanner();
    } else if (cmd === CommandValue.TrialModeOff) {
      window.removeTrialModeBanner();
    } else if (cmd === CommandValue.Checkin || cmd === CommandValue.ReAuth) {
      const checkinAction = config.tv?.checkin_action;
      this.legacyLogService.add('Events: checkin');
      if (checkinAction === 'ignore') {
        this.legacyLogService.add('Events: ignore checkin (wait for reboot)');
        this.logger.debug(`[EventsService.processCmd] ignore checkin (wait for reboot)`);
      } else {
        window.tv_auth();
      }
    } else if (cmd === CommandValue.Checkout) {
      this.legacyLogService.add('Events: checkout');
      $(window.HotezaTV).trigger('checkout');

      const checkoutAction = config.tv?.checkout_action;

      if (checkoutAction === 'reload') {
        this.legacyPersistentLogService.add('checkout', LegacyPersistentLogCategory.Reload);
        this.legacyLogService.add('TV: reload on checkout');
        window.reload_app();
      } else if (checkoutAction === 'restart') {
        this.legacyPersistentLogService.add('checkout', LegacyPersistentLogCategory.Reboot);
        window.tv_reboot();
      } else if (checkoutAction === 'poweroff') {
        this.legacyPersistentLogService.add('checkout', LegacyPersistentLogCategory.PowerOff);
        window.tv_poweroff();
      } else {
        window.tv_auth();
      }
    } else if (cmd === CommandValue.ContentUpdate) {
      this.legacyPersistentLogService.add('content update', LegacyPersistentLogCategory.Reload);
      this.legacyLogService.add('Events: content_update, reloading');
      window.reload_app();
    } else if (cmd === CommandValue.Restart) {
      this.legacyPersistentLogService.add('event restart', LegacyPersistentLogCategory.Reload);
      this.legacyLogService.add('Events: restart, reloading');
      window.reload_app();
    } else if (cmd === CommandValue.GetState) {
      void this.send('state', JSON.stringify(window.tv_get_state()));
    } else if (cmd === CommandValue.ParentalLockDisable || cmd === CommandValue.PinReset) {
      window.parental_lock_disable(true);
    } else if (cmd === CommandValue.Wol) {
      window
        .tv_power()
        .done((power_status) => {
          if (power_status) {
            //Получение команд от сервера (может там будильник)
            window.tv_get_server_commands();
          } else {
            this.legacyPersistentLogService.add('event wol', LegacyPersistentLogCategory.PowerOn);
            window.tv_poweron();
          }
        })
        .fail(() => {
          this.legacyPersistentLogService.add('event wol (get power fail)', LegacyPersistentLogCategory.PowerOn);
          window.tv_poweron();
        });
    } else if (cmd === CommandValue.PowerOff) {
      this.legacyPersistentLogService.add('event poweroff', LegacyPersistentLogCategory.PowerOff);
      window.tv_poweroff();
    } else if (cmd === CommandValue.FireAlarm) {
      if (window.tv_virtual_standby_state === false) {
        window.fire_alarm();
      }
    } else {
      this.legacyLogService.add('Events: unknown sys event ' + cmd);
      this.logger.warn(`[EventsService] Unknown sys event ${cmd}!`);
    }
  }

  private isValidTVID(tvId: string | number): boolean {
    if (tvId === '' || tvId === 0 || typeof tvId === 'undefined') {
      return true;
    }

    return tvId == this.TVID();
  }
}

export enum EventCategory {
  RC = 'rc',
  RCU = 'rcu',
  Marketing = 'marketing',
  Shop = 'shop',
  Feedback = 'feedback',
  Sys = 'sys',
}

type EvaluateResult = {
  status: boolean;
  message?: string;
};

type ServerEventHandler = (data: ServerEventData) => void;

export type ServerEventData = {
  cat: EventCategory;
  ch: string | number;
  cmd: CommandValue;
  data: object | KeyType | CustomKeyType;
};

type ApiEvent = {
  text: {c: string};
  tag: number;
  time: number;
};

export type SettingKey = 'language';

export interface RcChangeCommandData {
  settingKey: SettingKey;
  value: LanguageCode;
}

export interface WebSocketDeviceCommandMessage {
  type: 'command';
  module: EventCategory;
  payload?: {
    command: CommandValue;
    data: KeyType | CustomKeyType | RcChangeCommandData;
  };
  meta?: {
    fromIntegrationId: string;
    timestamp: string;
  };
  replyTo?: string;
  correlationId?: string;
}

export type CommandMessage = WebSocketDeviceCommandMessage & {
  payload: NonNullable<WebSocketDeviceCommandMessage['payload']>;
};

export interface WebSocketResponseMessage {
  type: 'response';
  replyTo?: string;
  correlationId?: string;
  state: 'executed' | 'failed';
  deviceState: Record<string, unknown>;
}

export enum CommandValue {
  TrialModeOn = 'trial_mode_on',
  TrialModeOff = 'trial_mode_off',
  Checkin = 'checkin',
  ReAuth = 'reauth',
  Checkout = 'checkout',
  ContentUpdate = 'content_update',
  Restart = 'restart',
  GetState = 'getstate',
  ParentalLockDisable = 'parental_lock_disable',
  PinReset = 'pin_reset',
  Wol = 'wol',
  PowerOff = 'poweroff',
  FireAlarm = 'file_alarm',
  Key = 'key',
  Change = 'change',

  OrderUpdate = 'orderUpdate',
  OrderUpdateData = 'orderUpdateData',
}

declare global {
  interface Window {
    Events: EventsService;
  }

  const Events: EventsService;
}
